import { audio } from '../../../audio/AudioManager.js';
import { SaveManager } from '../../../state/SaveManager.js';
import { applicationLifecycle } from '../../shared/lifecycle/index.js';
import {
  DISCOVERY_LAB_ASSETS,
  getMissingDiscoveryLabOpeningAssets,
} from './DiscoveryLabAssets.js';
import {
  DISCOVERY_LAB_DISTRICTS,
  DISCOVERY_LAB_LAYOUT,
  DISCOVERY_LAB_OPENING_SLICE,
  type DiscoveryLabDistrict,
} from './DiscoveryLabConfig.js';
import {
  completeDiscoveryLabInvestigation,
  discoverPollinatorGarden,
  discoverWashedOutTrail,
  getDiscoveryLabState,
  isMaterialsWorkshopRouteAvailable,
  isPollinatorGardenRouteAvailable,
  visitDiscoveryLabDistrict,
  type DiscoveryLabStateSnapshot,
} from './DiscoveryLabState.js';
import {
  calculateDiscoveryLabSafeBounds,
  isCompactDiscoveryLabLayout,
} from './components/DiscoveryLabLayout.js';
import {
  calculateOpaqueImageBaseline,
  DISCOVERY_LAB_COSMO_OPAQUE_BOUNDS,
} from './components/DiscoveryLabSafeLayout.js';
import { FieldCampusInputController } from './components/FieldCampusInputController.js';
import { ErosionInvestigation } from './minigames/ErosionInvestigation.js';
import { ErosionInvestigationView } from './minigames/ErosionInvestigationView.js';
import {
  getMissingPollinatorGardenAssets,
  POLLINATOR_GARDEN_ASSETS,
} from './pollinator-garden/PollinatorGardenAssets.js';
import { enterPollinatorGardenInvestigation } from './pollinator-garden/PollinatorGardenInvestigation.js';
import { PollinatorGardenInvestigationView } from './pollinator-garden/PollinatorGardenInvestigationView.js';
import { MATERIALS_WORKSHOP_SCENE_KEY } from './materials-workshop/MaterialsWorkshopScene.js';
import { WEATHER_WATCH_SCENE_KEY } from './weather-watch-station/WeatherWatchScene.js';
import { HABITAT_FIELD_SCENE_KEY } from './habitat-field-camp/HabitatFieldScene.js';
import { PLANT_GROWTH_SCENE_KEY } from './plant-growth-house/PlantGrowthScene.js';
import { LIFE_CYCLE_SCENE_KEY } from './life-cycle-pond/LifeCycleScene.js';
import { SOUND_VIBRATION_SCENE_KEY } from './sound-vibration-deck/SoundVibrationScene.js';
import { EARTH_MATERIALS_SCENE_KEY } from './earth-materials-yard/EarthMaterialsScene.js';
import { DISCOVERY_OBSERVATORY_SCENE_KEY } from './discovery-observatory/DiscoveryObservatoryScene.js';

const FONT_UI = '"Nunito", "Segoe UI", Arial, sans-serif';
const COSMO_START_X = 275;
const COSMO_BASELINE_Y = 662;
const TRAIL_SITE_X = 1072;
const TRAIL_SITE_Y = 419;
const GARDEN_SITE_X = 880;

type CampusDestinationId =
  | 'moving-water'
  | 'pollinator-garden'
  | 'materials-workshop'
  | 'weather-watch'
  | 'habitat-field-camp'
  | 'plant-growth-house'
  | 'life-cycle'
  | 'sound-vibration'
  | 'earth-materials'
  | 'observatory';

interface CampusDestinationDefinition {
  readonly id: CampusDestinationId;
  readonly label: string;
  readonly shortLabel: string;
  readonly x: number;
  readonly y: number;
  readonly district?: DiscoveryLabDistrict;
  readonly sceneKey?: string;
  readonly prerequisite: string;
}

interface CampusDestinationView {
  readonly definition: CampusDestinationDefinition;
  readonly marker: Phaser.GameObjects.Image;
  readonly sign: Phaser.GameObjects.Image;
  readonly label: Phaser.GameObjects.Text;
}

const CAMPUS_DESTINATIONS: readonly CampusDestinationDefinition[] = [
  { id: 'moving-water', label: 'MOVING WATER / EROSION', shortLabel: 'MOVING WATER', x: 1010, y: 470, prerequisite: 'Ready to investigate' },
  { id: 'pollinator-garden', label: 'POLLINATOR GARDEN', shortLabel: 'POLLINATOR GARDEN', x: 318, y: 382, district: DISCOVERY_LAB_DISTRICTS.pollinatorGarden, prerequisite: 'Restore the washed-out trail first' },
  { id: 'materials-workshop', label: 'MATERIALS WORKSHOP', shortLabel: 'MATERIALS', x: 420, y: 532, district: DISCOVERY_LAB_DISTRICTS.materialsWorkshop, sceneKey: MATERIALS_WORKSHOP_SCENE_KEY, prerequisite: 'Restore Pollinator Garden first' },
  { id: 'weather-watch', label: 'WEATHER WATCH', shortLabel: 'WEATHER WATCH', x: 342, y: 230, district: DISCOVERY_LAB_DISTRICTS.weatherWatchStation, sceneKey: WEATHER_WATCH_SCENE_KEY, prerequisite: 'Repair the Materials Workshop first' },
  { id: 'habitat-field-camp', label: 'HABITAT FIELD CAMP', shortLabel: 'HABITAT CAMP', x: 535, y: 315, district: DISCOVERY_LAB_DISTRICTS.habitatFieldCamp, sceneKey: HABITAT_FIELD_SCENE_KEY, prerequisite: 'Complete Weather Watch first' },
  { id: 'plant-growth-house', label: 'PLANT GROWTH HOUSE', shortLabel: 'GROWTH HOUSE', x: 680, y: 284, district: DISCOVERY_LAB_DISTRICTS.plantGrowthHouse, sceneKey: PLANT_GROWTH_SCENE_KEY, prerequisite: 'Complete Habitat Field Camp first' },
  { id: 'life-cycle', label: 'LIFE CYCLE POND', shortLabel: 'LIFE CYCLE', x: 760, y: 482, district: DISCOVERY_LAB_DISTRICTS.lifeCyclePond, sceneKey: LIFE_CYCLE_SCENE_KEY, prerequisite: 'Complete Plant Growth House first' },
  { id: 'sound-vibration', label: 'SOUND & VIBRATION', shortLabel: 'SOUND & VIBRATION', x: 875, y: 390, district: DISCOVERY_LAB_DISTRICTS.soundVibrationDeck, sceneKey: SOUND_VIBRATION_SCENE_KEY, prerequisite: 'Complete Life Cycle Pond first' },
  { id: 'earth-materials', label: 'EARTH MATERIALS', shortLabel: 'EARTH MATERIALS', x: 935, y: 300, district: DISCOVERY_LAB_DISTRICTS.earthMaterialsYard, sceneKey: EARTH_MATERIALS_SCENE_KEY, prerequisite: 'Complete Sound & Vibration first' },
  { id: 'observatory', label: 'DISCOVERY OBSERVATORY', shortLabel: 'OBSERVATORY', x: 1085, y: 205, district: DISCOVERY_LAB_DISTRICTS.discoveryObservatory, sceneKey: DISCOVERY_OBSERVATORY_SCENE_KEY, prerequisite: 'Complete Earth Materials first' },
] as const;

/** Production Discovery Lab field-campus arrival and Washed-Out Trail slice. */
export class DiscoveryLabScene extends Phaser.Scene {
  private renderScale = 1;
  private reducedMotion = false;
  private returningToHub = false;
  private controlsReady = false;
  private nearGardenSite = false;
  private nearCampusRoute = false;
  private nearMaterialsRoute = false;
  private activeDistrict: DiscoveryLabDistrict = DISCOVERY_LAB_DISTRICTS.fieldCampus;
  private resizeDebounce?: number;
  private unsubscribeLifecycle?: () => void;

  private environmentLayer!: Phaser.GameObjects.Container;
  private gameplayLayer!: Phaser.GameObjects.Container;
  private characterLayer!: Phaser.GameObjects.Container;
  private uiLayer!: Phaser.GameObjects.Container;
  private cosmo!: Phaser.GameObjects.Image;
  private cosmoShadow?: Phaser.GameObjects.Image;
  private campusForeground?: Phaser.GameObjects.Image;
  private tutorialNote?: Phaser.GameObjects.Image;
  private tutorialText?: Phaser.GameObjects.Text;
  private tutorialVisible = false;
  private lastCosmoX = COSMO_START_X;
  private selectedDestinationId: CampusDestinationId = 'moving-water';
  private destinationViews: CampusDestinationView[] = [];
  private ambientSprites: Phaser.GameObjects.Image[] = [];
  private trailVisual?: Phaser.GameObjects.Image;
  private gardenBackground?: Phaser.GameObjects.Image;
  private gardenSiteVisual?: Phaser.GameObjects.Image;
  private gardenPatchVisual?: Phaser.GameObjects.Image;
  private gardenAttention?: Phaser.GameObjects.Image;
  private gardenCampusFrame?: Phaser.GameObjects.Image;
  private materialsRouteFrame?: Phaser.GameObjects.Image;
  private materialsRouteLabel?: Phaser.GameObjects.Text;
  private backControl!: Phaser.GameObjects.Container;
  private backFrame!: Phaser.GameObjects.Image;
  private backLabel!: Phaser.GameObjects.Text;
  private titlePanel!: Phaser.GameObjects.Image;
  private worldTitle!: Phaser.GameObjects.Text;
  private missionLabel!: Phaser.GameObjects.Text;
  private controlsPanel?: Phaser.GameObjects.Image;
  private controlsHint!: Phaser.GameObjects.Text;
  private inputController!: FieldCampusInputController;
  private worldState!: DiscoveryLabStateSnapshot;
  private investigation!: ErosionInvestigation;
  private activeInvestigationView?: ErosionInvestigationView;
  private activeRelationshipView?: PollinatorGardenInvestigationView;

  constructor() { super({ key: 'DiscoveryLab' }); }

  create() {
    const missingAssets = [
      ...getMissingDiscoveryLabOpeningAssets(this),
      ...getMissingPollinatorGardenAssets(this),
    ];
    if (missingAssets.length > 0) {
      console.warn(
        '[DiscoveryLab] Opening route blocked because production textures are missing:',
        missingAssets.map(asset => asset.path),
      );
      this.scene.start('HomeHub');
      return;
    }

    void audio.enterSoundscape('discovery-lab');
    this.renderScale = this.configureCrispRenderer();
    this.reducedMotion = SaveManager.getSettings().reducedMotion
      || (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
    this.returningToHub = false;
    this.controlsReady = false;
    this.nearGardenSite = false;
    this.nearCampusRoute = false;
    this.nearMaterialsRoute = false;
    this.destinationViews = [];
    this.ambientSprites = [];
    this.selectedDestinationId = 'moving-water';
    this.tutorialVisible = false;
    this.lastCosmoX = COSMO_START_X;

    const savedState = getDiscoveryLabState();
    const resumeGarden = savedState.currentDistrict === DISCOVERY_LAB_DISTRICTS.pollinatorGarden
      && savedState.unlockedDistricts.includes(DISCOVERY_LAB_DISTRICTS.pollinatorGarden)
      && isPollinatorGardenRouteAvailable();
    this.activeDistrict = resumeGarden
      ? DISCOVERY_LAB_DISTRICTS.pollinatorGarden
      : DISCOVERY_LAB_DISTRICTS.fieldCampus;
    const arrival = visitDiscoveryLabDistrict(this.activeDistrict);
    this.worldState = arrival.state;
    if (this.activeDistrict === DISCOVERY_LAB_DISTRICTS.fieldCampus) {
      this.investigation = this.createOpeningInvestigation();
    }

    this.createLayerArchitecture();
    if (this.activeDistrict === DISCOVERY_LAB_DISTRICTS.pollinatorGarden) {
      this.createPollinatorGardenEnvironment();
    } else {
      this.createFieldCampusEnvironment();
    }
    this.createCosmo();
    this.createExplorationInput();
    this.createSafeNavigation();
    this.registerLifecycleSafety();
    this.publishStateToRegistry();
    this.playArrivalReveal(arrival.firstVisit);
  }

  private configureCrispRenderer() {
    const parentWidth = this.scale.parentSize.width || window.innerWidth;
    const parentHeight = this.scale.parentSize.height || window.innerHeight;
    const cssScale = Math.max(
      parentWidth / DISCOVERY_LAB_LAYOUT.width,
      parentHeight / DISCOVERY_LAB_LAYOUT.height,
    );
    const dpr = Phaser.Math.Clamp(window.devicePixelRatio || 1, 1, 2);
    const renderScale = Phaser.Math.Clamp(cssScale * dpr, 1, 2);

    this.scale.scaleMode = Phaser.Scale.ENVELOP;
    this.scale.autoCenter = Phaser.Scale.NO_CENTER;
    this.scale.displaySize.setAspectMode(Phaser.Scale.ENVELOP);
    this.scale.setGameSize(
      Math.round(DISCOVERY_LAB_LAYOUT.width * renderScale),
      Math.round(DISCOVERY_LAB_LAYOUT.height * renderScale),
    );
    this.scale.refresh();
    this.game.canvas.style.marginLeft = '0px';
    this.game.canvas.style.marginTop = '0px';
    this.cameras.main
      .setZoom(renderScale)
      .centerOn(DISCOVERY_LAB_LAYOUT.width * 0.5, DISCOVERY_LAB_LAYOUT.height * 0.5);
    return renderScale;
  }

  private createLayerArchitecture() {
    this.environmentLayer = this.add.container(0, 0).setDepth(0).setName('discovery-lab-environment');
    this.gameplayLayer = this.add.container(0, 0).setDepth(20).setName('discovery-lab-gameplay');
    this.characterLayer = this.add.container(0, 0).setDepth(40).setName('discovery-lab-character');
    this.uiLayer = this.add.container(0, 0).setDepth(100).setName('discovery-lab-ui');
  }

  private createFieldCampusEnvironment() {
    const campus = this.add.image(
      DISCOVERY_LAB_LAYOUT.width * 0.5,
      DISCOVERY_LAB_LAYOUT.height * 0.5,
      DISCOVERY_LAB_ASSETS.fieldCampusHub.key,
    ).setDisplaySize(DISCOVERY_LAB_LAYOUT.width, DISCOVERY_LAB_LAYOUT.height);
    this.environmentLayer.add(campus);

    CAMPUS_DESTINATIONS.forEach(definition => this.createCampusDestination(definition));
    const movingWater = this.destinationViews.find(view => view.definition.id === 'moving-water');
    this.trailVisual = movingWater?.marker;

    this.campusForeground = this.add.image(
      DISCOVERY_LAB_LAYOUT.width * 0.5,
      DISCOVERY_LAB_LAYOUT.height * 0.5,
      DISCOVERY_LAB_ASSETS.campusForeground.key,
    ).setDisplaySize(DISCOVERY_LAB_LAYOUT.width, DISCOVERY_LAB_LAYOUT.height)
      .setAlpha(0.96);
    this.environmentLayer.add(this.campusForeground);
    if (!this.reducedMotion) {
      this.tweens.add({
        targets: this.campusForeground,
        x: { from: DISCOVERY_LAB_LAYOUT.width * 0.5 - 2, to: DISCOVERY_LAB_LAYOUT.width * 0.5 + 2 },
        y: { from: DISCOVERY_LAB_LAYOUT.height * 0.5 + 1, to: DISCOVERY_LAB_LAYOUT.height * 0.5 - 2 },
        duration: 4200,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      this.createCampusAmbientLife();
    }
    this.createMovementTutorial();
  }

  private createCampusDestination(definition: CampusDestinationDefinition) {
    const marker = this.add.image(
      definition.x,
      definition.y,
      DISCOVERY_LAB_ASSETS.destinationMarker.key,
    ).setDisplaySize(54, 54).setInteractive(
      new Phaser.Geom.Circle(256, 256, 256),
      Phaser.Geom.Circle.Contains,
    ).setDepth(30 + definition.y / 100);
    marker.input!.cursor = 'pointer';

    const sign = this.add.image(
      definition.x,
      definition.y - 52,
      DISCOVERY_LAB_ASSETS.destinationSignpost.key,
    ).setDisplaySize(168, 56).setInteractive({ useHandCursor: true })
      .setDepth(34 + definition.y / 100).setVisible(false);
    const label = this.add.text(definition.x, definition.y - 52, definition.label, {
      fontFamily: FONT_UI,
      fontSize: '10px',
      fontStyle: 'bold',
      color: '#fff8df',
      stroke: '#063f43',
      strokeThickness: 3,
      align: 'center',
      wordWrap: { width: 132 },
    }).setOrigin(0.5).setDepth(35 + definition.y / 100).setVisible(false);

    marker.on('pointerover', () => this.selectCampusDestination(definition.id));
    marker.on('pointerup', () => this.activateCampusDestination(definition));
    sign.on('pointerup', () => this.activateCampusDestination(definition));
    this.gameplayLayer.add([marker, sign, label]);
    this.destinationViews.push({ definition, marker, sign, label });
  }

  private createCampusAmbientLife() {
    const butterfly = this.add.image(380, 350, POLLINATOR_GARDEN_ASSETS.butterfly.key)
      .setDisplaySize(34, 28).setAlpha(0.82).setDepth(27);
    const bee = this.add.image(560, 405, POLLINATOR_GARDEN_ASSETS.bee.key)
      .setDisplaySize(25, 25).setAlpha(0.76).setDepth(27);
    this.ambientSprites = [butterfly, bee];
    this.gameplayLayer.add(this.ambientSprites);
    this.tweens.add({
      targets: butterfly,
      x: '+=120',
      y: '-=28',
      angle: 6,
      duration: 6200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: bee,
      x: '-=85',
      y: '+=18',
      duration: 4700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private createMovementTutorial() {
    this.tutorialVisible = true;
    this.tutorialNote = this.add.image(
      0,
      0,
      DISCOVERY_LAB_ASSETS.tutorialFieldNote.key,
    ).setDisplaySize(250, 117).setDepth(104);
    this.tutorialText = this.add.text(0, 0, 'Tap a path or use  ←  →  to explore', {
      fontFamily: FONT_UI,
      fontSize: '12px',
      fontStyle: 'bold',
      color: '#174c4d',
      align: 'center',
      wordWrap: { width: 172 },
    }).setOrigin(0.5).setDepth(105);
    this.uiLayer.add([this.tutorialNote, this.tutorialText]);
  }

  private dismissMovementTutorial() {
    if (!this.tutorialVisible) return;
    this.tutorialVisible = false;
    if (this.reducedMotion) {
      this.tutorialNote?.setVisible(false);
      this.tutorialText?.setVisible(false);
      return;
    }
    this.tweens.add({
      targets: [this.tutorialNote, this.tutorialText],
      alpha: 0,
      y: '-=8',
      duration: 260,
      onComplete: () => {
        this.tutorialNote?.setVisible(false);
        this.tutorialText?.setVisible(false);
      },
    });
  }

  private isCampusDestinationAvailable(definition: CampusDestinationDefinition) {
    if (definition.id === 'moving-water') return true;
    if (definition.id === 'pollinator-garden') return isPollinatorGardenRouteAvailable();
    if (definition.id === 'materials-workshop') return isMaterialsWorkshopRouteAvailable();
    return Boolean(
      definition.district
      && this.worldState.unlockedDistricts.includes(definition.district),
    );
  }

  private selectCampusDestination(id: CampusDestinationId) {
    this.selectedDestinationId = id;
    this.refreshCampusDestinations();
  }

  private refreshCampusDestinations() {
    const compact = isCompactDiscoveryLabLayout(this);
    this.destinationViews.forEach(view => {
      const available = this.isCampusDestinationAvailable(view.definition);
      const selected = view.definition.id === this.selectedDestinationId;
      const markerSize = (compact ? 48 : 54) * (selected ? 1.12 : 1);
      view.marker.clearTint()
        .setAlpha(available ? (selected ? 1 : 0.88) : 0.66)
        .setDisplaySize(markerSize, markerSize);
      if (!available) view.marker.setTint(0x839b8d);
      view.sign.setVisible(selected).setAlpha(available ? 1 : 0.78);
      const labelText = compact ? view.definition.shortLabel : view.definition.label;
      view.label.setVisible(selected).setColor(available ? '#fff8df' : '#d8dfd1');
      if (view.label.text !== labelText) view.label.setText(labelText);
      if (selected && this.missionLabel) {
        this.missionLabel.setText(
          available
            ? `${view.definition.shortLabel} • READY`
            : `${view.definition.shortLabel} • LOCKED`,
        );
      }
    });
  }

  private activateCampusDestination(definition: CampusDestinationDefinition) {
    if (!this.controlsReady || this.activeInvestigationView || this.activeRelationshipView) return;
    this.selectCampusDestination(definition.id);
    this.dismissMovementTutorial();
    if (!this.isCampusDestinationAvailable(definition)) {
      audio.hint();
      return;
    }
    if (definition.id === 'moving-water') {
      this.openInvestigationFromTrail();
      return;
    }
    if (definition.id === 'pollinator-garden') {
      this.travelToPollinatorGarden();
      return;
    }
    if (!definition.sceneKey) return;
    this.controlsReady = false;
    this.inputController.disable();
    SaveManager.save();
    this.cameras.main.fadeOut(this.reducedMotion ? 180 : 360, 190, 235, 225);
    this.time.delayedCall(this.reducedMotion ? 200 : 380, () => {
      this.scene.start(definition.sceneKey!);
    });
  }

  private createPollinatorGardenEnvironment() {
    this.gardenBackground = this.add.image(
      DISCOVERY_LAB_LAYOUT.width * 0.5,
      DISCOVERY_LAB_LAYOUT.height * 0.5,
      this.currentGardenBackgroundTexture(),
    ).setDisplaySize(1580, 720);
    this.environmentLayer.add(this.gardenBackground);

    this.gardenSiteVisual = this.add.image(
      GARDEN_SITE_X,
      305,
      POLLINATOR_GARDEN_ASSETS.missionPanel.key,
    ).setDisplaySize(255, 176).setInteractive({ useHandCursor: true });
    this.gardenPatchVisual = this.add.image(
      GARDEN_SITE_X,
      492,
      this.currentGardenPatchTexture(),
    ).setDisplaySize(250, 202).setInteractive({ useHandCursor: true });
    if (this.worldState.pollinatorGardenRestoration !== 'restored') {
      this.gardenPatchVisual.setCrop(54, 0, 234, 315);
    }
    const siteLabel = this.add.text(GARDEN_SITE_X, 620, 'POLLINATOR FIELD STATION', {
      fontFamily: FONT_UI,
      fontSize: '16px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#173821',
      strokeThickness: 5,
    }).setOrigin(0.5);
    this.gardenSiteVisual.on('pointerup', this.openPollinatorGardenFromSite, this);
    this.gardenPatchVisual.on('pointerup', this.openPollinatorGardenFromSite, this);
    this.gameplayLayer.add([this.gardenSiteVisual, this.gardenPatchVisual, siteLabel]);

    this.gardenCampusFrame = this.add.image(
      205,
      330,
      DISCOVERY_LAB_ASSETS.labMissionPanel.key,
    ).setCrop(135, 55, 930, 300).setDisplaySize(180, 78)
      .setInteractive({ useHandCursor: true });
    const campusLabel = this.add.text(205, 330, '←  FIELD CAMPUS', {
      fontFamily: FONT_UI,
      fontSize: '13px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#173821',
      strokeThickness: 5,
    }).setOrigin(0.5);
    this.gardenCampusFrame.on('pointerup', this.returnToFieldCampus, this);
    this.gameplayLayer.add([this.gardenCampusFrame, campusLabel]);

    this.gardenAttention = this.add.image(
      GARDEN_SITE_X - 150,
      430,
      POLLINATOR_GARDEN_ASSETS.pollenTransferVfx.key,
    ).setDisplaySize(290, 132).setAlpha(this.reducedMotion ? 0.46 : 0.3);
    this.gameplayLayer.add(this.gardenAttention);
    if (!this.reducedMotion && this.worldState.pollinatorGardenRestoration !== 'restored') {
      this.tweens.add({
        targets: this.gardenAttention,
        alpha: { from: 0.2, to: 0.62 },
        duration: 1300,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
    this.applyGardenRestorationState();
    this.createMaterialsWorkshopRoute();
  }

  private createMaterialsWorkshopRoute() {
    const safe = calculateDiscoveryLabSafeBounds(this);
    this.materialsRouteFrame = this.add.image(
      safe.right - 118,
      safe.y + 234,
      'materials-workshop-mission-panel',
    ).setDisplaySize(210, 70).setInteractive({ useHandCursor: true });
    this.materialsRouteLabel = this.add.text(
      safe.right - 118,
      safe.y + 234,
      'MATERIALS WORKSHOP  →',
      {
        fontFamily: FONT_UI,
        fontSize: '13px',
        fontStyle: 'bold',
        color: '#ffffff',
        stroke: '#173821',
        strokeThickness: 5,
      },
    ).setOrigin(0.5);
    this.materialsRouteFrame.on('pointerup', this.travelToMaterialsWorkshop, this);
    this.gameplayLayer.add([this.materialsRouteFrame, this.materialsRouteLabel]);
    this.updateMaterialsWorkshopRouteState();
  }

  private createCosmo() {
    const startX = this.activeDistrict === DISCOVERY_LAB_DISTRICTS.pollinatorGarden ? 260 : COSMO_START_X;
    this.cosmoShadow = this.add.image(
      startX,
      COSMO_BASELINE_Y - 7,
      DISCOVERY_LAB_ASSETS.cosmoContactShadow.key,
    ).setDisplaySize(112, 34).setAlpha(0.56);
    this.characterLayer.add(this.cosmoShadow);
    this.cosmo = this.add.image(
      startX,
      COSMO_BASELINE_Y,
      DISCOVERY_LAB_ASSETS.cosmo.key,
    ).setOrigin(0.5, 1).setDisplaySize(138, 184);
    this.characterLayer.add(this.cosmo);
  }

  private createExplorationInput() {
    this.inputController = new FieldCampusInputController(this, this.cosmo, {
      minX: 220,
      maxX: this.activeDistrict === DISCOVERY_LAB_DISTRICTS.pollinatorGarden ? 1090 : 1120,
      baselineY: COSMO_BASELINE_Y,
      speed: 205,
      depth: 55,
      onPositionChanged: x => {
        this.cosmoShadow?.setX(x);
        if (Math.abs(x - this.lastCosmoX) > 3) {
          this.lastCosmoX = x;
          this.dismissMovementTutorial();
        }
        this.nearGardenSite = this.activeDistrict === DISCOVERY_LAB_DISTRICTS.pollinatorGarden
          && Math.abs(x - GARDEN_SITE_X) <= 180;
        this.nearCampusRoute = this.activeDistrict === DISCOVERY_LAB_DISTRICTS.pollinatorGarden
          && x <= 345;
        this.nearMaterialsRoute = this.activeDistrict === DISCOVERY_LAB_DISTRICTS.pollinatorGarden
          && isMaterialsWorkshopRouteAvailable()
          && x >= 985;
        if (
          this.activeDistrict === DISCOVERY_LAB_DISTRICTS.fieldCampus
          && !this.tutorialVisible
        ) {
          const nearest = this.destinationViews
            .map(view => ({ view, distance: Math.abs(view.marker.x - x) }))
            .filter(entry => entry.distance <= 88)
            .sort((a, b) => a.distance - b.distance)[0];
          if (nearest && nearest.view.definition.id !== this.selectedDestinationId) {
            this.selectCampusDestination(nearest.view.definition.id);
          }
        }
        this.updateControlsHint();
      },
    });
    this.input.keyboard?.on('keydown-ESC', this.handleEscape, this);
    this.input.keyboard?.on('keydown-E', this.handleInteract, this);
    this.input.keyboard?.on('keydown-W', this.travelToMaterialsWorkshop, this);
  }

  private createSafeNavigation() {
    const safe = calculateDiscoveryLabSafeBounds(this);
    this.backControl = this.add.container(safe.x + 68, safe.y + 35);
    this.backFrame = this.add.image(0, 0, DISCOVERY_LAB_ASSETS.destinationSignpost.key)
      .setDisplaySize(144, 48)
      .setInteractive({ useHandCursor: true });
    this.backLabel = this.add.text(
      0,
      0,
      '←  HOME',
      {
      fontFamily: FONT_UI,
      fontSize: '14px',
      fontStyle: 'bold',
      color: '#fff8df',
      stroke: '#063f43',
      strokeThickness: 3,
      },
    ).setOrigin(0.5);
    this.backFrame.on('pointerup', this.handleBackNavigation, this);
    this.backControl.add([this.backFrame, this.backLabel]);

    this.titlePanel = this.add.image(
      safe.centerX,
      safe.y + 47,
      DISCOVERY_LAB_ASSETS.fieldJournalPlaque.key,
    ).setDisplaySize(420, 144);
    this.worldTitle = this.add.text(
      safe.centerX,
      safe.y + 39,
      this.activeDistrict === DISCOVERY_LAB_DISTRICTS.pollinatorGarden
        ? 'POLLINATOR GARDEN'
        : 'DISCOVERY LAB',
      {
      fontFamily: FONT_UI,
      fontSize: '22px',
      fontStyle: 'bold',
      color: '#fff8df',
      stroke: '#174c4d',
      strokeThickness: 4,
      },
    ).setOrigin(0.5);
    this.missionLabel = this.add.text(
      safe.centerX,
      safe.y + 76,
      this.activeDistrict === DISCOVERY_LAB_DISTRICTS.pollinatorGarden
        ? 'DISCOVERY LAB • PLANT + ANIMAL RELATIONSHIPS'
        : 'FIELD CAMPUS • MOVING WATER',
      {
      fontFamily: FONT_UI,
      fontSize: '12px',
      fontStyle: 'bold',
      color: '#174c4d',
      stroke: '#f8eed4',
      strokeThickness: 2,
      },
    ).setOrigin(0.5);
    this.controlsPanel = this.add.image(
      safe.centerX,
      safe.bottom - 54,
      DISCOVERY_LAB_ASSETS.destinationSignpost.key,
    ).setDisplaySize(270, 90).setVisible(false);
    this.controlsHint = this.add.text(safe.centerX, safe.bottom - 54, '', {
      fontFamily: FONT_UI,
      fontSize: '12px',
      fontStyle: 'bold',
      color: '#fff8df',
      stroke: '#063f43',
      strokeThickness: 3,
      align: 'center',
      wordWrap: { width: 220 },
    }).setOrigin(0.5).setVisible(false);
    this.uiLayer.add([
      this.backControl,
      this.titlePanel,
      this.worldTitle,
      this.missionLabel,
      this.controlsPanel,
      this.controlsHint,
    ]);
    // Apply safe bounds on the first rendered frame as well as later resizes.
    // Otherwise Cosmo kept the 1280x720 baseline until a window resize event.
    this.positionSafeUI();
  }

  private createOpeningInvestigation() {
    return new ErosionInvestigation(DISCOVERY_LAB_OPENING_SLICE.investigation, {
      onStateChanged: snapshot => {
        this.registry.set('discoveryLab:activeInvestigation', snapshot);
      },
      onCompleted: (_result, config) => {
        const completion = completeDiscoveryLabInvestigation(
          config.id,
          config.completionWorldEvent,
        );
        this.worldState = completion.state;
        this.publishStateToRegistry();
        this.applyTrailRestorationState();
        if (completion.newlyCompleted) {
          this.events.emit('discovery-lab:world-event', config.completionWorldEvent);
        }
      },
    });
  }

  private openInvestigationFromTrail() {
    if (
      this.activeDistrict !== DISCOVERY_LAB_DISTRICTS.fieldCampus
      || !this.controlsReady
      || this.activeInvestigationView
      || this.activeRelationshipView
    ) return;
    this.openInvestigation();
  }

  private openInvestigation() {
    const discovery = discoverWashedOutTrail();
    this.worldState = discovery.state;
    this.publishStateToRegistry();
    this.controlsReady = false;
    this.inputController.disable();
    this.uiLayer.setVisible(false);
    this.activeInvestigationView = new ErosionInvestigationView(this, this.investigation, {
      reducedMotion: this.reducedMotion,
      onExit: () => this.closeInvestigation(false),
      onComplete: () => this.closeInvestigation(true),
    });
    this.events.emit('discovery-lab:open-investigation', this.investigation);
  }

  private closeInvestigation(completed: boolean) {
    if (!this.activeInvestigationView) return;
    this.activeInvestigationView.destroy();
    this.activeInvestigationView = undefined;
    this.uiLayer.setVisible(true);
    this.controlsReady = true;
    if (document.hasFocus()) this.inputController.enable();
    this.updateControlsHint();
    if (completed) this.playFieldRestorationReaction();
  }

  private openPollinatorGardenFromSite() {
    if (
      this.activeDistrict !== DISCOVERY_LAB_DISTRICTS.pollinatorGarden
      || !this.controlsReady
      || this.activeInvestigationView
      || this.activeRelationshipView
    ) return;
    this.openPollinatorGardenInvestigation();
  }

  private openPollinatorGardenInvestigation() {
    const entry = enterPollinatorGardenInvestigation();
    if (!entry.available) {
      console.warn('[DiscoveryLab] Pollinator Garden investigation unavailable:', entry.reason);
      return;
    }
    this.worldState = getDiscoveryLabState();
    this.publishStateToRegistry();
    this.controlsReady = false;
    this.inputController.disable();
    this.uiLayer.setVisible(false);
    this.activeRelationshipView = new PollinatorGardenInvestigationView(
      this,
      entry.investigation,
      {
        reducedMotion: this.reducedMotion,
        onExit: () => this.closePollinatorGardenInvestigation(false),
        onComplete: () => this.closePollinatorGardenInvestigation(true),
      },
    );
    this.events.emit('discovery-lab:open-relationship-investigation', entry.investigation);
  }

  private closePollinatorGardenInvestigation(completed: boolean) {
    if (!this.activeRelationshipView) return;
    this.activeRelationshipView.destroy();
    this.activeRelationshipView = undefined;
    this.worldState = getDiscoveryLabState();
    this.uiLayer.setVisible(true);
    this.controlsReady = true;
    if (document.hasFocus()) this.inputController.enable();
    this.updateControlsHint();
    if (completed) this.playGardenRestorationReaction();
  }

  private playFieldRestorationReaction() {
    this.applyTrailRestorationState();
    const burst = this.add.image(
      this.trailVisual?.x ?? TRAIL_SITE_X,
      (this.trailVisual?.y ?? TRAIL_SITE_Y) - 5,
      DISCOVERY_LAB_ASSETS.restorationVfx.key,
    ).setDepth(75).setDisplaySize(300, 323)
      .setBlendMode(Phaser.BlendModes.ADD).setAlpha(0);
    if (this.reducedMotion) burst.setAlpha(0.6);
    else {
      this.tweens.add({
        targets: burst,
        alpha: 0.65,
        duration: 380,
        yoyo: true,
        hold: 500,
        ease: 'Sine.easeOut',
        onComplete: () => burst.destroy(),
      });
    }
    if (this.reducedMotion) this.time.delayedCall(900, () => burst.destroy());
  }

  private applyTrailRestorationState() {
    this.refreshCampusDestinations();
  }

  private currentGardenBackgroundTexture() {
    return this.worldState.pollinatorGardenRestoration === 'restored'
      ? POLLINATOR_GARDEN_ASSETS.gardenRestored.key
      : POLLINATOR_GARDEN_ASSETS.gardenDormant.key;
  }

  private currentGardenPatchTexture() {
    return this.worldState.pollinatorGardenRestoration === 'restored'
      ? POLLINATOR_GARDEN_ASSETS.flowerPatchSeedPods.key
      : POLLINATOR_GARDEN_ASSETS.flowerPatchVisited.key;
  }

  private applyGardenRestorationState() {
    this.gardenBackground?.setTexture(this.currentGardenBackgroundTexture()).setDisplaySize(1580, 720);
    this.gardenPatchVisual?.setTexture(this.currentGardenPatchTexture()).setDisplaySize(250, 202);
    if (this.gardenPatchVisual) {
      if (this.worldState.pollinatorGardenRestoration === 'restored') {
        this.gardenPatchVisual.setCrop(0, 0, 408, 315);
      } else {
        this.gardenPatchVisual.setCrop(54, 0, 234, 315);
      }
    }
    this.gardenAttention?.setVisible(this.worldState.pollinatorGardenRestoration !== 'restored');
    this.updateMaterialsWorkshopRouteState();
  }

  private updateMaterialsWorkshopRouteState() {
    const available = isMaterialsWorkshopRouteAvailable();
    this.materialsRouteFrame?.setVisible(available);
    this.materialsRouteLabel?.setVisible(available);
    if (this.materialsRouteFrame?.input) this.materialsRouteFrame.input.enabled = available;
  }

  private playGardenRestorationReaction() {
    this.applyGardenRestorationState();
    const burst = this.add.image(
      GARDEN_SITE_X - 80,
      390,
      POLLINATOR_GARDEN_ASSETS.restorationVfx.key,
    ).setDepth(80).setDisplaySize(500, 239).setAlpha(0);
    if (this.reducedMotion) burst.setAlpha(0.78);
    else {
      this.tweens.add({
        targets: burst,
        alpha: 0.88,
        duration: 520,
        yoyo: true,
        hold: 750,
        ease: 'Sine.easeOut',
        onComplete: () => burst.destroy(),
      });
    }
    if (this.reducedMotion) this.time.delayedCall(1050, () => burst.destroy());
  }

  private travelToPollinatorGarden() {
    if (
      this.activeDistrict !== DISCOVERY_LAB_DISTRICTS.fieldCampus
      || !this.controlsReady
      || !isPollinatorGardenRouteAvailable()
      || this.activeInvestigationView
      || this.activeRelationshipView
    ) return;
    const discovery = discoverPollinatorGarden();
    if (!discovery.unlocked) return;
    this.controlsReady = false;
    this.inputController.disable();
    this.cameras.main.fadeOut(this.reducedMotion ? 180 : 380, 190, 235, 180);
    this.time.delayedCall(this.reducedMotion ? 200 : 400, () => this.scene.restart());
  }

  private returnToFieldCampus() {
    if (
      this.activeDistrict !== DISCOVERY_LAB_DISTRICTS.pollinatorGarden
      || this.activeInvestigationView
      || this.activeRelationshipView
    ) return;
    this.controlsReady = false;
    this.inputController.disable();
    visitDiscoveryLabDistrict(DISCOVERY_LAB_DISTRICTS.fieldCampus);
    this.cameras.main.fadeOut(this.reducedMotion ? 180 : 380, 190, 235, 180);
    this.time.delayedCall(this.reducedMotion ? 200 : 400, () => this.scene.restart());
  }

  private readonly travelToMaterialsWorkshop = () => {
    if (
      this.activeDistrict !== DISCOVERY_LAB_DISTRICTS.pollinatorGarden
      || !this.controlsReady
      || !isMaterialsWorkshopRouteAvailable()
      || this.activeInvestigationView
      || this.activeRelationshipView
    ) return;
    this.controlsReady = false;
    this.inputController.disable();
    SaveManager.save();
    this.scene.start(MATERIALS_WORKSHOP_SCENE_KEY);
  };

  private handleBackNavigation() {
    this.returnToHub();
  }

  private publishStateToRegistry() {
    this.registry.set('discoveryLab:visited', this.worldState.visited);
    this.registry.set('discoveryLab:currentDistrict', this.worldState.currentDistrict);
    this.registry.set('discoveryLab:fieldCampusDiscovered', this.worldState.fieldCampusDiscovered);
    this.registry.set(
      'discoveryLab:washedOutTrailDiscovered',
      this.worldState.washedOutTrailDiscovered,
    );
    this.registry.set(
      'discoveryLab:investigationsCompleted',
      [...this.worldState.investigationsCompleted],
    );
    this.registry.set(
      'discoveryLab:washedOutTrailRestoration',
      this.worldState.washedOutTrailRestoration,
    );
    this.registry.set(
      'discoveryLab:pollinatorGardenRouteAvailable',
      isPollinatorGardenRouteAvailable(),
    );
    this.registry.set(
      'discoveryLab:pollinatorGardenDiscovered',
      this.worldState.pollinatorGardenDiscovered,
    );
    this.registry.set(
      'discoveryLab:pollinatorGardenRestoration',
      this.worldState.pollinatorGardenRestoration,
    );
    this.registry.set(
      'discoveryLab:materialsWorkshopRouteAvailable',
      isMaterialsWorkshopRouteAvailable(),
    );
    this.registry.set(
      'discoveryLab:investigationsStarted',
      [...this.worldState.investigationsStarted],
    );
    this.registry.set(
      'discoveryLab:evidenceRecords',
      { ...this.worldState.evidenceRecords },
    );
  }

  private playArrivalReveal(firstVisit: boolean) {
    this.controlsReady = true;
    if (document.hasFocus()) this.inputController.enable();
    if (this.reducedMotion || !firstVisit) return;
    this.cameras.main.fadeIn(500, 190, 235, 255);
  }

  private updateControlsHint(
    safe = calculateDiscoveryLabSafeBounds(this),
    compact = isCompactDiscoveryLabLayout(this, safe),
  ) {
    if (!this.controlsHint) return;
    let text = '';
    if (this.activeDistrict === DISCOVERY_LAB_DISTRICTS.pollinatorGarden) {
      const restored = this.worldState?.pollinatorGardenRestoration === 'restored';
      if (this.nearMaterialsRoute) {
        text = compact
          ? 'E/W travel • Materials Workshop'
          : 'Press E or tap the sign to visit Materials Workshop';
      } else if (this.nearCampusRoute) {
        text = compact
          ? 'E return • Field Campus'
          : 'Press E or tap the sign to return to Field Campus';
      } else if (this.nearGardenSite) {
        text = restored
          ? compact ? 'E review • garden restored' : 'Press E to review the restored garden investigation'
          : compact ? 'E investigate • tap station' : 'Press E or tap the field station to investigate';
      }
    } else {
      const selected = this.destinationViews.find(
        view => view.definition.id === this.selectedDestinationId,
      );
      if (selected && Math.abs(this.cosmo.x - selected.marker.x) <= 90) {
        text = this.isCampusDestinationAvailable(selected.definition)
          ? compact
            ? 'E or tap • explore'
            : `Press E or tap the beacon to explore ${selected.definition.label}`
          : selected.definition.prerequisite;
      }
    }
    this.controlsHint.setText(text);
    this.controlsHint.setVisible(Boolean(text));
    this.controlsPanel?.setVisible(Boolean(text));
    this.positionControlsHint(safe, compact);
  }

  private positionControlsHint(
    safe = calculateDiscoveryLabSafeBounds(this),
    compact = isCompactDiscoveryLabLayout(this, safe),
  ) {
    const wrapWidth = Math.min(compact ? 190 : 230, safe.width - 40);
    this.controlsHint
      .setFontSize(compact ? 10 : 12)
      .setWordWrapWidth(wrapWidth)
      .updateText();
    const y = safe.bottom - (compact ? 42 : 50);
    this.controlsHint.setPosition(safe.centerX, y);
    this.controlsPanel?.setPosition(safe.centerX, y)
      .setDisplaySize(compact ? 230 : 270, compact ? 76 : 90);
  }

  private handleInteract() {
    if (this.activeInvestigationView || this.activeRelationshipView) return;
    if (this.activeDistrict === DISCOVERY_LAB_DISTRICTS.pollinatorGarden) {
      if (this.nearMaterialsRoute) this.travelToMaterialsWorkshop();
      else if (this.nearCampusRoute) this.returnToFieldCampus();
      else if (this.nearGardenSite) this.openPollinatorGardenInvestigation();
      return;
    }
    const selected = this.destinationViews.find(
      view => view.definition.id === this.selectedDestinationId,
    );
    if (selected && Math.abs(this.cosmo.x - selected.marker.x) <= 96) {
      this.activateCampusDestination(selected.definition);
    }
  }

  private handleEscape() {
    if (this.activeInvestigationView || this.activeRelationshipView) return;
    this.handleBackNavigation();
  }

  private returnToHub() {
    if (this.returningToHub || this.activeInvestigationView || this.activeRelationshipView) return;
    this.returningToHub = true;
    this.controlsReady = false;
    this.inputController.disable();
    this.cameras.main.fadeOut(this.reducedMotion ? 180 : 380, 190, 235, 255);
    this.time.delayedCall(this.reducedMotion ? 200 : 400, () => {
      this.scale.setGameSize(DISCOVERY_LAB_LAYOUT.width, DISCOVERY_LAB_LAYOUT.height);
      this.cameras.main
        .setZoom(1)
        .centerOn(DISCOVERY_LAB_LAYOUT.width * 0.5, DISCOVERY_LAB_LAYOUT.height * 0.5);
      this.scene.start('HomeHub');
    });
  }

  private registerLifecycleSafety() {
    this.unsubscribeLifecycle = applicationLifecycle.subscribe({
      onSuspend: () => this.handlePause(),
      onResume: () => this.handleResume(),
    });
    window.addEventListener('resize', this.handleWindowResize);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.unsubscribeLifecycle?.();
      this.unsubscribeLifecycle = undefined;
      audio.leaveSoundscape({ id: 'discovery-lab' });
      window.removeEventListener('resize', this.handleWindowResize);
      if (this.resizeDebounce !== undefined) window.clearTimeout(this.resizeDebounce);
      this.input.keyboard?.off('keydown-ESC', this.handleEscape, this);
      this.input.keyboard?.off('keydown-E', this.handleInteract, this);
      this.input.keyboard?.off('keydown-W', this.travelToMaterialsWorkshop, this);
      this.gardenSiteVisual?.off('pointerup', this.openPollinatorGardenFromSite, this);
      this.gardenPatchVisual?.off('pointerup', this.openPollinatorGardenFromSite, this);
      this.gardenCampusFrame?.off('pointerup', this.returnToFieldCampus, this);
      this.materialsRouteFrame?.off('pointerup', this.travelToMaterialsWorkshop, this);
      this.backFrame.off('pointerup', this.handleBackNavigation, this);
      this.activeInvestigationView?.destroy();
      this.activeInvestigationView = undefined;
      this.activeRelationshipView?.destroy();
      this.activeRelationshipView = undefined;
      this.tweens.killAll();
    });
  }

  private handlePause() {
    this.inputController.disable();
    this.activeInvestigationView?.setSuspended(true);
    this.activeRelationshipView?.setSuspended(true);
  }

  private handleResume() {
    this.activeInvestigationView?.setSuspended(false);
    this.activeRelationshipView?.setSuspended(false);
    if (
      this.controlsReady
      && !this.returningToHub
      && !this.activeInvestigationView
      && !this.activeRelationshipView
    ) {
      this.inputController.enable();
    }
  }

  private readonly handleWindowResize = () => {
    if (this.resizeDebounce !== undefined) window.clearTimeout(this.resizeDebounce);
    this.resizeDebounce = window.setTimeout(() => {
      if (!this.scene.isActive()) return;
      this.renderScale = this.configureCrispRenderer();
      this.positionSafeUI();
      this.activeInvestigationView?.layout();
      this.activeRelationshipView?.layout();
    }, 80);
  };

  private positionSafeUI() {
    const safe = calculateDiscoveryLabSafeBounds(this);
    const compact = isCompactDiscoveryLabLayout(this, safe);
    this.destinationViews.forEach(view => {
      const x = Phaser.Math.Clamp(view.definition.x, safe.x + 38, safe.right - 38);
      const y = Phaser.Math.Clamp(
        view.definition.y,
        safe.y + (compact ? 118 : 126),
        safe.bottom - (compact ? 54 : 64),
      );
      view.marker.setPosition(x, y).setDisplaySize(compact ? 48 : 54, compact ? 48 : 54);
      view.sign.setPosition(x, Math.max(safe.y + (compact ? 100 : 112), y - (compact ? 46 : 52)))
        .setDisplaySize(compact ? 142 : 168, compact ? 48 : 56);
      view.label.setPosition(view.sign.x, view.sign.y)
        .setFontSize(compact ? 9 : 10)
        .setWordWrapWidth(compact ? 112 : 132);
    });
    this.backControl.setPosition(safe.x + (compact ? 60 : 70), safe.y + (compact ? 29 : 32));
    this.backFrame.setDisplaySize(compact ? 120 : 140, compact ? 44 : 48);
    this.backLabel.setFontSize(compact ? 11 : 13);
    this.titlePanel.setPosition(safe.centerX, safe.y + (compact ? 54 : 66))
      .setDisplaySize(compact ? 340 : 400, compact ? 116 : 137);
    this.worldTitle.setPosition(safe.centerX, safe.y + (compact ? 32 : 39))
      .setFontSize(compact ? 18 : 22);
    this.missionLabel.setPosition(safe.centerX, safe.y + (compact ? 91 : 108))
      .setFontSize(compact ? 8 : 9)
      .setWordWrapWidth(compact ? 230 : 286);
    this.tutorialNote?.setPosition(
      safe.right - (compact ? 126 : 145),
      safe.y + (compact ? 138 : 158),
    ).setDisplaySize(compact ? 218 : 250, compact ? 102 : 117);
    this.tutorialText?.setPosition(
      safe.right - (compact ? 120 : 137),
      safe.y + (compact ? 138 : 158),
    ).setFontSize(compact ? 9 : 11).setWordWrapWidth(compact ? 126 : 148);
    const cosmoHeight = compact ? 160 : 184;
    const cosmoBaseline = Math.min(
      COSMO_BASELINE_Y,
      calculateOpaqueImageBaseline(
        safe.bottom,
        cosmoHeight,
        DISCOVERY_LAB_COSMO_OPAQUE_BOUNDS.sourceHeight,
        DISCOVERY_LAB_COSMO_OPAQUE_BOUNDS.bottomExclusive,
        compact ? 12 : 16,
      ),
    );
    this.cosmo?.setY(cosmoBaseline).setDisplaySize(cosmoHeight * 0.75, cosmoHeight);
    this.cosmoShadow?.setPosition(this.cosmo?.x ?? COSMO_START_X, cosmoBaseline - 7)
      .setDisplaySize(compact ? 98 : 112, compact ? 30 : 34);
    this.inputController?.setBaselineY(cosmoBaseline);
    this.materialsRouteFrame?.setPosition(safe.right - 118, safe.y + 234);
    this.materialsRouteLabel?.setPosition(safe.right - 118, safe.y + 234);
    if (this.activeDistrict === DISCOVERY_LAB_DISTRICTS.fieldCampus) {
      this.refreshCampusDestinations();
    }
    this.updateControlsHint(safe, compact);
  }
}
